<template>
  <!-- PC 端遥操作 HUD：管理连接、虚拟摇杆、PS4 手柄、速度和日志。 -->
  <div class="teleop-layer" :class="{ 'teleop-layer--active': active }">
    <!-- 激活后的控制界面（叠加在视频上） -->
    <template v-if="active">
      <!-- 顶部信息区下方的遥控控制组：窄窗口时自动换行，不遮挡编号、镜头和时间。 -->
      <div class="teleop-topbar" :class="{ 'teleop-topbar--en': lang === 'en' }">
        <div class="teleop-statusbar">
          <span class="state-dot" :class="connectionState"></span>
          <span class="state-text">{{ stateLabels[connectionState] || connectionState }}</span>
          <span class="speed-label">{{ translate('teleop_speed') }}</span>
          <div class="speed-selector" role="group" :aria-label="translate('teleop_speed_mode')">
            <button
              v-for="speed in speedModes"
              :key="speed.value"
              type="button"
              class="speed-option"
              :class="{ 'speed-option--selected': speedMode === speed.value, 'speed-option--disabled': !isConnected }"
              :disabled="!isConnected || speedModePending"
              @click="changeSpeedMode(speed.value)"
            >{{ speed.label }}</button>
          </div>

          <template v-if="isConnected">
            <button
              class="pill-btn"
              :class="{ 'pill-btn--active': teleopEnabled }"
              @click="toggleEnable"
            >{{ translate(teleopEnabled ? 'teleop_stop' : 'teleop_start') }}</button>
          </template>
          <button
            v-else
            class="pill-btn pill-btn--primary"
            :disabled="isConnecting"
            @click="connect"
          >{{ translate(isConnecting ? 'teleop_connecting' : 'teleop_connect') }}</button>

          <button class="pill-close" @click="close" :title="translate('teleop_close')">×</button>
        </div>

        <!-- 手柄图标、连接状态和日志入口与连接操作视为同一组。 -->
        <div class="teleop-device-toolbar" :class="{ 'teleop-device-toolbar--connected': gamepadConnected }">
          <span class="device-icon" :title="gamepadName || translate('teleop_gamepad_name')">
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path fill="currentColor" d="M7 6h10a5 5 0 0 1 5 5v6a3 3 0 0 1-3 3h-1.5a2 2 0 0 1-1.7-.94L14.6 17H9.4l-1.2 2.06A2 2 0 0 1 6.5 20H5a3 3 0 0 1-3-3v-6a5 5 0 0 1 5-5zm-1.5 5A1.5 1.5 0 1 0 4 9.5 1.5 1.5 0 0 0 5.5 11zM9 8.5H7.5v1.5H9V11.5h1.5V10H12V8.5h-1.5V7H9v1.5zm8.5.5a1.25 1.25 0 1 0 0 2.5 1.25 1.25 0 0 0 0-2.5zm-2.5 3a1.25 1.25 0 1 0 0 2.5 1.25 1.25 0 0 0 0-2.5z"/>
            </svg>
          </span>
          <span class="device-state-dot"></span>
          <span class="device-state-text">{{ translate(gamepadConnected ? 'teleop_gamepad_connected' : 'teleop_gamepad_disconnected') }}</span>
          <button class="device-log-button" type="button" :title="translate('teleop_view_log')" @click="showLog = !showLog">
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path fill="currentColor" d="M5 4h14a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2zm2 4v2h10V8H7zm0 4v2h10v-2H7zm0 4v2h6v-2H7z"/>
            </svg>
            <span>{{ translate('teleop_log') }}</span>
          </button>
          <!-- 日志面板跟随日志入口定位，始终从工具组下沿展开。 -->
          <div class="teleop-log" v-if="showLog">
            <div class="log-header" @click="showLog = false">
              <span>{{ translate('teleop_log') }}</span>
              <span>{{ translate('teleop_collapse') }}</span>
            </div>
            <div class="log-body">
              <div v-for="(msg, i) in logs" :key="i" class="log-line" :class="logClass(msg)">{{ msg }}</div>
            </div>
          </div>
        </div>
      </div>

      <!-- 虚拟摇杆（左右下角） -->
      <JoystickControl
        :disabled="!isConnected || !teleopEnabled"
        :enable-gamepad-visual="isConnected && teleopEnabled"
        @change="onJoystickChange"
        @gamepad="onGamepadState"
        @gamepad-movement="onGamepadMovement"
      />

      <!-- 手柄移动提示（未开启遥控时） -->
      <div class="teleop-gamepad-hint" v-if="showGamepadHint">
        <span class="hint-icon">🎮</span>
        <span class="hint-text">{{ translate('teleop_start_hint') }}</span>
      </div>

    </template>
  </div>
</template>

<script>
// Copyright (c) 2026 Rotunbot, Inc. All rights reserved.
// 遥操作层：叠加在视频卡片上，管理连接 + PS4 手柄 + 状态
import api from '@/api';
import TeleoperationConnection from '@/utils/teleopConnection';
import langMap from '@/utils/language';
import JoystickControl from './JoystickControl.vue';
import teleopDefaultIcon from '@/assets/teleoperation/ps4-controller-default.png';
import teleopSelectedIcon from '@/assets/teleoperation/ps4-controller-selected.png';

// 各轴中位值
const NEUTRAL = { throttle: 500, roll: 0, yaw: 0, pitch: 0 };

// 私钥接口返回值可能被 API 包装在一层或多层 data 中；只取字段值，不记录私钥内容。
function findPrivateKey(value, depth = 0) {
  if (depth > 4 || value === undefined || value === null) return '';
  if (typeof value === 'string') {
    const text = value.trim();
    if (!text) return '';
    try {
      return findPrivateKey(JSON.parse(text), depth + 1) || text;
    } catch (e) {
      return text;
    }
  }
  if (Array.isArray(value)) {
    for (let i = 0; i < value.length; i++) {
      const key = findPrivateKey(value[i], depth + 1);
      if (key) return key;
    }
    return '';
  }
  if (typeof value !== 'object') return '';
  const direct = value.privateKey || value.private_key;
  if (typeof direct === 'string' && direct.trim()) return direct.trim();
  return findPrivateKey(value.data, depth + 1) || findPrivateKey(value.result, depth + 1);
}

export default {
  name: 'TeleoperationIndex',
  components: { JoystickControl },
  props: {
    robotId: { type: [Number, String], default: null },
    robotCode: { type: [Number, String], default: '' },
    iceServers: { type: Array, default: () => [] },
    // 由父组件 v-model 控制
    value: { type: Boolean, default: false }
  },
  data() {
    return {
      active: false,
      connection: null,
      connectionState: 'idle', // idle|signalling|connecting|connected|disconnected|error
      isConnecting: false,
      teleopEnabled: false,
      speedMode: null,
      speedModePending: false,
      resolvedRobotCode: '',

      // 摇杆状态（协议值）
      joystickData: { yaw: 0, throttle: 500, roll: 0, pitch: 0, buttons: 0 },
      gamepadConnected: false,
      gamepadName: '',
      // 手柄移动提示
      showGamepadHint: false,
      gamepadHintTimer: null,
      // 遥控确认对话框倒计时
      enableConfirmCountdown: 0,
      enableConfirmTimer: null,

      logs: [],
      maxLogs: 30,
      showLog: false,
      autoDisableTimer: null,
      activationToken: 0
    };
  },
  computed: {
    lang() {
      return this.$store.state.app.language || 'zh';
    },
    stateLabels() {
      return {
        idle: this.translate('teleop_status_idle'),
        signalling: this.translate('teleop_status_signalling'),
        connecting: this.translate('teleop_status_negotiating'),
        connected: this.translate('teleop_status_connected'),
        disconnected: this.translate('teleop_status_disconnected'),
        error: this.translate('teleop_status_error')
      };
    },
    isConnected() {
      return this.connectionState === 'connected';
    },
    speedModes() {
      return [
        { value: 1, label: this.translate('teleop_speed_low') },
        { value: 2, label: this.translate('teleop_speed_medium') },
        { value: 3, label: this.translate('teleop_speed_high') }
      ];
    }
  },
  watch: {
    // 支持父组件 v-model（teleopVisibles 映射）
    value(val) {
      this.setActive(val);
    },
    robotId(val, oldVal) {
      if (val !== oldVal) this.handleRobotChanged();
    },
    robotCode(val, oldVal) {
      if (val !== oldVal) this.handleRobotChanged();
    }
  },
  mounted() {
    if (this.value) {
      this.setActive(true);
    }
    // 只在浏览器页签隐藏时关闭遥控使能，不响应窗口失焦。
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
  },
  beforeDestroy() {
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    this.clearAutoDisableTimer();
    if (this.gamepadHintTimer) {
      clearTimeout(this.gamepadHintTimer);
      this.gamepadHintTimer = null;
    }
    if (this.enableConfirmTimer) {
      clearInterval(this.enableConfirmTimer);
      this.enableConfirmTimer = null;
    }
    this.destroyConnection();
  },
  deactivated() {
    // Vue keep-alive 内部页签切换时释放遥操作会话。
    this.finishDeactivate();
  },
  methods: {
    // Read teleoperation strings from the same zh/en dictionary used by the rest of the app.
    translate(key, values = {}) {
      const entry = langMap[key] || {};
      let text = entry[this.lang] || entry.zh || key;
      Object.keys(values).forEach((name) => {
        text = text.replace(new RegExp('\\{' + name + '\\}', 'g'), String(values[name]));
      });
      return text;
    },
    // Translate the common connection-manager messages shown in the on-screen log.
    translateConnectionLog(message) {
      const text = String(message || '').replace(/^\d{2}:\d{2}:\d{2}\s+/, '').replace(/^\[ERROR\]\s*/, '');
      const exactMessages = {
        'Offer 已发送，等待 Answer': 'teleop_log_offer_sent',
        '设置 Answer 完成': 'teleop_log_answer_set',
        '添加 Candidate 完成': 'teleop_log_candidate_added',
        '连接已取消': 'teleop_connection_cancelled',
        'Candidate 添加失败（SDP 中已包含，忽略）': 'teleop_log_candidate_ignored',
        'ICE 连接失败': 'teleop_log_ice_failed',
        'ICE 连接已断开': 'teleop_log_ice_disconnected',
        'PeerConnection 连接失败': 'teleop_log_peer_failed',
        '收到机器人 Offer；当前协议要求浏览器作为 Offerer': 'teleop_log_unexpected_robot_offer',
        '收到 Answer 时 PeerConnection 不存在': 'teleop_log_missing_peer_for_answer',
        '收到 Candidate 时 PeerConnection 不存在': 'teleop_log_missing_peer_for_candidate',
        '机器人工号为空': 'teleop_log_robot_code_missing',
        '私钥未加载': 'teleop_log_private_key_missing',
        '信令连接失败': 'teleop_log_signaling_failed',
        '处理信令消息失败': 'teleop_log_signaling_message_failed',
        '连接已关闭': 'teleop_log_connection_closed',
        '密钥为空': 'teleop_error_empty_key',
        '仅支持 PKCS#8 私钥（BEGIN PRIVATE KEY），不支持 PKCS#1 私钥': 'teleop_error_pkcs8_only',
        '仅支持 SPKI 公钥（BEGIN PUBLIC KEY），不支持 PKCS#1 公钥': 'teleop_error_spki_only',
        '密钥不是有效的 Base64 编码': 'teleop_error_invalid_base64',
        '协议未初始化，请先调用 initProtocol()': 'teleop_error_protocol_uninitialized',
        '信令连接已关闭（WebRTC 连接成功，信令协商完成）': 'teleop_log_signaling_complete',
        '遥操作信令会话已关闭，控制权可能已转移': 'teleop_log_session_replaced',
        '设备遥控权已转移给其他客户端，当前连接已断开': 'teleop_notice_control_transferred',
        'joystick 控制量已回中': 'teleop_log_joystick_neutral',
        'command 通道未就绪': 'teleop_log_command_channel_unready',
        '默认中速模式设置失败': 'teleop_log_default_speed_failed',
        '上行不是 Envelope，尝试按裸响应解析': 'teleop_log_raw_response_fallback',
        '解析 command 上行失败': 'teleop_log_command_parse_failed',
        '发送停止遥控命令失败，继续关闭连接': 'teleop_log_stop_command_failed',
        '信令 bye 已发送，请求移除遥操作会话': 'teleop_log_signaling_bye_sent',
        '发送信令 bye 失败': 'teleop_log_signaling_bye_failed'
      };
      if (exactMessages[text]) return this.translate(exactMessages[text]);

      let match = text.match(/^joystick 控制量开始发送: yaw=(-?\d+), throttle=(-?\d+), roll=(-?\d+), pitch=(-?\d+)$/);
      if (match) return this.translate('teleop_log_joystick_motion', { yaw: match[1], throttle: match[2], roll: match[3], pitch: match[4] });
      match = text.match(/^joystick 帧已发送: interval=(\d+)ms, yaw=(-?\d+), throttle=(-?\d+), roll=(-?\d+), pitch=(-?\d+)$/);
      if (match) return this.translate('teleop_log_joystick_frame', { interval: match[1], yaw: match[2], throttle: match[3], roll: match[4], pitch: match[5] });
      match = text.match(/^收到 telemetry (\d+) bytes$/);
      if (match) return this.translate('teleop_log_telemetry_received', { bytes: match[1] });
      match = text.match(/^收到 command 文本上行: (.*)$/);
      if (match) return this.translate('teleop_log_command_text_received', { data: match[1] });
      match = text.match(/^收到 command 二进制上行: (\d+) bytes$/);
      if (match) return this.translate('teleop_log_command_binary_received', { bytes: match[1] });
      match = text.match(/^上行 Envelope: method=(.*), raw=(\d+) bytes, signedPayload=(\d+) bytes$/);
      if (match) return this.translate('teleop_log_envelope_summary', { method: match[1], rawBytes: match[2], signedBytes: match[3] });
      match = text.match(/^回执不匹配: expected Envelope，actual=裸二进制，expectedMethod=(.*)$/);
      if (match) return this.translate('teleop_log_mismatch_envelope', { method: match[1] });
      match = text.match(/^回执不匹配: method，expected=(.*), actual=(.*)$/);
      if (match) return this.translate('teleop_log_mismatch_method', { expected: match[1], actual: match[2] });
      match = text.match(/^临时兼容回执 method: expected=(.*), actual=(.*)$/);
      if (match) return this.translate('teleop_log_compat_method', { expected: match[1], actual: match[2] });
      match = text.match(/^回执不匹配: response body，expectedType=(.*), rawBytes=(\d+)$/);
      if (match) return this.translate('teleop_log_mismatch_body', { type: match[1], bytes: match[2] });
      match = text.match(/^回执业务 payload: length=(\d+), hex=(.*), text=(.*)$/);
      if (match) return this.translate('teleop_log_response_payload', { length: match[1], hex: match[2], text: match[3] });
      match = text.match(/^回执不匹配: request_id，expected=(.*), actual=(.*)$/);
      if (match) return this.translate('teleop_log_mismatch_request_id', { expected: match[1], actual: match[2] });
      match = text.match(/^上行响应不是预期 Protobuf: (.*)$/);
      if (match) return this.translate('teleop_log_unexpected_protobuf', { type: match[1] });
      match = text.match(/^command 执行失败: (.*)$/);
      if (match) return this.translate('teleop_log_command_failed', { details: match[1] });
      match = text.match(/^默认中速模式设置失败: (.*)$/);
      if (match) return this.translate('teleop_log_default_speed_failed_detail', { message: match[1] });
      match = text.match(/^发送 joystick 失败/);
      if (match) return this.translate('teleop_log_joystick_send_failed');
      match = text.match(/^(joystick|command|telemetry) 通道已关闭$/);
      if (match) return this.translate('teleop_log_channel_closed_error', { channel: match[1] });
      match = text.match(/^channel \[(.+)\] 已打开$/);
      if (match) return this.translate('teleop_log_channel_open', { channel: match[1] });
      match = text.match(/^channel \[(.+)\] 已关闭$/);
      if (match) return this.translate('teleop_log_channel_closed', { channel: match[1] });
      match = text.match(/^channel \[(.+)\] 错误$/);
      if (match) return this.translate('teleop_log_channel_error', { channel: match[1] });
      match = text.match(/^ICE 状态: (.+)$/);
      if (match) return this.translate('teleop_log_ice_state', { state: match[1] });
      match = text.match(/^等待设备响应超时（(\d+)s）$/);
      if (match) return this.translate('teleop_log_device_timeout', { seconds: match[1] });
      match = text.match(/^创建 Offer 失败: (.+)$/);
      if (match) return this.translate('teleop_log_offer_failed', { message: match[1] });
      match = text.match(/^信令错误: (.+)$/);
      if (match) return this.translate('teleop_log_signal_error', { message: match[1] });
      match = text.match(/^等待 command 回执超时: (.+)$/);
      if (match) return this.translate('teleop_log_response_timeout', { method: match[1] });
      match = text.match(/^command 已发送: (.+)$/);
      if (match) return this.translate('teleop_log_command_sent', { method: match[1] });
      match = text.match(/^command 回执匹配成功: method=(.+), request_id=(.+)$/);
      if (match) return this.translate('teleop_log_receipt_matched', { method: match[1], requestId: match[2] });
      return text;
    },
    /* ---------- 激活/关闭 ---------- */
    // 根据父组件 v-model 打开或关闭遥操作层。
    setActive(val) {
      if (!val) {
        if (this.active && (this.isConnecting || this.isConnected)) {
          this.$Modal.confirm({
            title: this.translate('teleop_cancel_title'),
            content: this.translate(this.isConnecting ? 'teleop_cancel_connecting' : 'teleop_cancel_connected'),
            okText: this.translate('teleop_confirm_cancel'),
            cancelText: this.translate('teleop_continue'),
            onOk: () => this.finishDeactivate(),
            onCancel: () => this.$emit('input', true)
          });
          return;
        }
        this.finishDeactivate();
        return;
      }
      if (this.active) return;
      const token = ++this.activationToken;
      this.isConnecting = true;
      this.active = true;
      if (val) {
        // 点击遥控图标后自动取钥并建立连接。
        this.ensureRobotCode().then(() => {
          if (!this.active || token !== this.activationToken) return;
          this.initConnection();
          this.connect(true);
        }).catch((error) => {
          if (token !== this.activationToken) return;
          this.isConnecting = false;
          this.connectionState = 'error';
          this.active = false;
          this.$emit('input', false);
          this.addLog(this.translate('teleop_log_connect_failed', { message: error.message || error }));
          this.$Notice.error({ title: this.translate('teleop_connect_error_title'), desc: this.translateConnectionLog(error.message || error), duration: 5 });
        });
        this.$emit('input', true);
      }
    },
    // 完成关闭动作，释放连接并同步父组件状态。
    finishDeactivate() {
      this.activationToken += 1;
      this.isConnecting = false;
      this.gamepadConnected = false;
      this.gamepadName = '';
      this.showGamepadHint = false;
      if (this.gamepadHintTimer) {
        clearTimeout(this.gamepadHintTimer);
        this.gamepadHintTimer = null;
      }
      if (this.enableConfirmTimer) {
        clearInterval(this.enableConfirmTimer);
        this.enableConfirmTimer = null;
      }
      this.enableConfirmCountdown = 0;
      // disconnect() 内部会发送停止遥控命令并关闭 WebSocket/PeerConnection。
      this.destroyConnection();
      this.active = false;
      this.$emit('input', false);
    },
    // 关闭按钮调用的统一入口。
    requestClose() {
      this.setActive(false);
    },
    // 兼容旧模板的关闭方法别名。
    close() {
      this.requestClose();
    },

    // robotCode 可能只传了 robotId（数字），需查详情拿 code。
    async ensureRobotCode() {
      this.resolvedRobotCode = this.robotCode ? String(this.robotCode) : '';
      const hasDistinctRobotCode = this.resolvedRobotCode && String(this.resolvedRobotCode) !== String(this.robotId);
      if (!hasDistinctRobotCode && this.robotId !== null && this.robotId !== undefined && this.robotId !== '') {
        try {
          const res = await api.getData('robot-server/robot/get/' + this.robotId, this);
          if (res.state == 1 && res.data) {
            const data = typeof res.data === 'string' ? JSON.parse(res.data) : res.data;
            const robot = data.data && typeof data.data === 'object' ? data.data : data;
            if (robot.code || robot.robotCode || robot.robot_code) {
              this.resolvedRobotCode = String(robot.code || robot.robotCode || robot.robot_code);
            }
          }
        } catch (e) {
          this.addLog(this.translate('teleop_log_robot_code_failed'));
        }
      }
      if (!this.resolvedRobotCode || (!hasDistinctRobotCode && this.resolvedRobotCode === String(this.robotId))) {
        throw new Error(this.translate('teleop_error_robot_code_missing'));
      }
    },

    /* ---------- 连接管理 ---------- */
    // 创建连接管理器并绑定状态、日志和回执回调。
    initConnection() {
      this.destroyConnection();
      this.connection = new TeleoperationConnection({
        robotId: this.robotId,
        robotCode: this.resolvedRobotCode || this.robotCode,
        iceServers: this.iceServers,
        onStateChange: (state) => {
          this.connectionState = state;
          if (state !== 'connected') {
            this.teleopEnabled = false;
            this.speedModePending = false;
            this.speedMode = null;
            this.resetControls();
            this.clearAutoDisableTimer();
          }
        },
        onCommandResponse: (envelope, response) => {
          const method = (envelope && envelope.header && envelope.header.method) || '';
          this.addLog(this.translate('teleop_log_response_received', { method }));
          
          // 处理速度模式回执，同步页面状态
          if (method === 'teleop.set_speed_mode' && response) {
            const mode = response.current_mode !== undefined ? response.current_mode : response.mode;
            if (mode !== undefined) {
              this.speedMode = mode;
              const modeLabel = (this.speedModes.find(item => item.value === mode) || {}).label || mode;
              this.addLog(this.translate('teleop_log_speed_synced', { mode: modeLabel }));
            }
          }
        },
        onError: (msg) => {
          const localizedMessage = this.translateConnectionLog(msg);
          this.addLog(this.translate('teleop_log_error', { message: localizedMessage }));
          this.$Notice.error({ title: this.translate('teleop_notice_title'), desc: localizedMessage, duration: 5 });
        },
        onLog: (msg) => this.addLog(this.translateConnectionLog(msg))
      });
    },

    // 机器人切换时销毁旧会话并重新激活。
    handleRobotChanged() {
      this.resolvedRobotCode = '';
      if (this.active) {
        this.finishDeactivate();
        this.$nextTick(() => this.setActive(true));
      }
    },

    // 关闭信令、PeerConnection 和 DataChannel。
    destroyConnection() {
      if (this.connection) {
        this.connection.disconnect();
        this.connection = null;
      }
      this.connectionState = 'idle';
      this.teleopEnabled = false;
      this.speedMode = null;
      this.speedModePending = false;
      this.clearAutoDisableTimer();
    },

    // 获取私钥并启动浏览器 Offer 协商。
    async connect(autoStart = false) {
      if (this.isConnecting && !autoStart) return;
      if (!this.connection) {
        // 失败或被切页后旧连接对象已被清理，点击连接时重新创建会话。
        this.initConnection();
      }
      this.isConnecting = true;

      try {
        const privateKeyPem = await this.loadKeys();
        await this.connection.connect(privateKeyPem);
      } catch (e) {
        const message = e.message || String(e);
        this.addLog(this.translate('teleop_log_connect_failed', { message: this.translateConnectionLog(message) }));
        if (!e.teleopReported) {
          this.$Notice.error({ title: this.translate('teleop_connect_error_title'), desc: this.translateConnectionLog(message), duration: 5 });
        }
        // 失败实例不可复用，下一次点击连接将创建新的 PeerConnection。
        this.destroyConnection();
      } finally {
        this.isConnecting = false;
      }
    },

    // 这里只通过管理接口取签名材料；遥操作业务数据仍由 WebRTC DataChannel 直达球端。
    // robot-server/robot/get/key/{robotId} → res.data.privateKey
    async loadKeys() {
      if (this.robotId === null || this.robotId === undefined || this.robotId === '') {
        throw new Error(this.translate('teleop_error_robot_id_missing'));
      }
      const res = await api.getData('robot-server/robot/get/key/' + encodeURIComponent(this.robotId), this);
      if (!res || res.state != 1) {
        throw new Error(this.translate('teleop_error_key_load', { message: (res && res.msg) || this.translate('teleop_no_response') }));
      }
      const key = findPrivateKey(res);
      if (!key) throw new Error(this.translate('teleop_error_key_missing'));
      this.addLog(this.translate('teleop_log_key_loaded'));
      return key;
    },

    clearAutoDisableTimer() {
      if (this.autoDisableTimer) clearTimeout(this.autoDisableTimer);
      this.autoDisableTimer = null;
    },

    // 启动/刷新 60 秒无操作自动关闭遥控使能定时器。
    refreshAutoDisableTimer() {
      this.clearAutoDisableTimer();
      if (!this.teleopEnabled) return;
      this.autoDisableTimer = setTimeout(() => {
        if (this.connection && this.teleopEnabled) {
          this.addLog(this.translate('teleop_log_idle_timeout'));
          this.disableTeleopForSafety(this.translate('teleop_reason_idle_timeout'));
        }
      }, 60000);
    },

    // 浏览器页签隐藏时只关闭遥控使能，保留连接。
    handlePageHidden() {
      return;
      this.resetControls();
      this.clearAutoDisableTimer();
      this.disableTeleopForSafety(this.translate('teleop_reason_tab_hidden'));
    },

    // 通过正式 set_enabled(false) 停止球端控制，保留 PeerConnection 和 DataChannel。
    async disableTeleopForSafety(reason) {
      if (!this.teleopEnabled) return;
      if (!this.connection || !this.isConnected) {
        this.teleopEnabled = false;
        return;
      }
      try {
        await this.connection.setTeleopEnabled(false);
        this.teleopEnabled = false;
        this.addLog(this.translate('teleop_log_disabled_keep_connection', { reason }));
      } catch (error) {
        this.teleopEnabled = false;
        this.connection.setJoystickEnabled(false);
        const message = this.translateConnectionLog(error.message || this.translate('teleop_error_no_receipt'));
        this.addLog(this.translate('teleop_log_disable_failed', { reason, message }));
        this.$Notice.error({ title: this.translate('teleop_safety_stop_failed'), desc: message, duration: 5 });
      }
    },

    handleVisibilityChange() {
      if (document.hidden) this.handlePageHidden();
    },

    /* ---------- 命令 ---------- */
    // 切换球端遥控使能状态。
    async toggleEnable() {
      if (!this.isConnected) return;
      // 如果是开始遥控，显示安全警告对话框
      if (!this.teleopEnabled) {
        this.showEnableConfirmDialog();
        return;
      }
      
      // 停止遥控直接执行
      try {
        this.resetControls();
        await this.connection.setTeleopEnabled(false);
        this.teleopEnabled = false;
        this.clearAutoDisableTimer();
        this.addLog(this.translate('teleop_log_stopped'));
      } catch (e) {
        this.$Notice.error({ title: this.translate('teleop_notice_title'), desc: this.translate('teleop_toggle_failed') });
      }
    },

    // 显示开始遥控确认对话框
    showEnableConfirmDialog() {
      this.enableConfirmCountdown = 5;
      
      this.$Modal.confirm({
        title: '<span style="color: #ff9800; font-size: 22px;">⚠</span> ' + this.translate('teleop_security_title'),
        content: this.translate('teleop_security_message'),
        okText: this.translate('teleop_confirm_countdown', { seconds: this.enableConfirmCountdown }),
        cancelText: this.translate('teleop_cancel'),
        onOk: () => {
          if (this.enableConfirmCountdown > 0) {
            return false; // 倒计时未结束，阻止确认
          }
          this.startTeleopEnable();
          return true;
        },
        onCancel: () => {
          if (this.enableConfirmTimer) {
            clearInterval(this.enableConfirmTimer);
            this.enableConfirmTimer = null;
          }
          this.enableConfirmCountdown = 0;
        }
      });

      // iView confirm 强制渲染默认问号图标；移除当前弹窗自己的图标，不影响其他确认框。
      this.$nextTick(() => {
        const modalWraps = document.querySelectorAll('.ivu-modal-wrap:not(.ivu-modal-hidden)');
        const modalWrap = modalWraps[modalWraps.length - 1];
        const confirmIcon = modalWrap && modalWrap.querySelector('.ivu-modal-confirm-head-icon');
        if (confirmIcon) confirmIcon.remove();
      });
      
      // 开始倒计时
      this.enableConfirmTimer = setInterval(() => {
        this.enableConfirmCountdown--;
        if (this.enableConfirmCountdown <= 0) {
          clearInterval(this.enableConfirmTimer);
          this.enableConfirmTimer = null;
          // 倒计时结束，启用确认按钮
          this.$nextTick(() => {
            const modalButtons = document.querySelectorAll('.ivu-modal-confirm .ivu-btn-primary');
            modalButtons.forEach(btn => {
              btn.disabled = false;
              btn.textContent = this.translate('teleop_confirm');
            });
          });
        } else {
          // 更新倒计时显示
          this.$nextTick(() => {
            const modalButtons = document.querySelectorAll('.ivu-modal-confirm .ivu-btn-primary');
            modalButtons.forEach(btn => {
              btn.disabled = true;
              btn.textContent = this.translate('teleop_confirm_countdown', { seconds: this.enableConfirmCountdown });
            });
          });
        }
      }, 1000);
    },

    // 执行开始遥控
    async startTeleopEnable() {
      if (!this.connection || !this.isConnected) {
        this.$Notice.warning({ title: this.translate('teleop_notice_title'), desc: this.translate('teleop_connect_data_first') });
        return;
      }
      try {
        this.resetControls();
        await this.connection.setTeleopEnabled(true);
        this.teleopEnabled = true;
        this.refreshAutoDisableTimer();
        this.addLog(this.translate('teleop_log_started'));
      } catch (e) {
        this.$Notice.error({ title: this.translate('teleop_notice_title'), desc: this.translate('teleop_toggle_failed') });
      }
    },

    /* ---------- 摇杆 ---------- */
    // 接收虚拟摇杆或 Gamepad 同步值并转发。
    onJoystickChange(data) {
      this.joystickData = { ...this.joystickData, ...data };
      if (this.teleopEnabled) this.refreshAutoDisableTimer();
      this.sendJoystick();
    },

    // 发送当前缓存的 ManualControl 值。
    sendJoystick() {
      if (this.connection && this.isConnected) {
        this.connection.sendJoystick(this.joystickData);
      }
    },

    // 将输入状态恢复为协议中位值。
    resetControls() {
      this.joystickData = { ...NEUTRAL, buttons: 0 };
    },

    // 发送低速、中速或高速模式命令。
    async changeSpeedMode(mode) {
      if (!this.isConnected || this.speedMode === mode || this.speedModePending) return;
      this.speedModePending = true;
      try {
        await this.connection.setSpeedMode(mode);
        this.speedMode = mode;
        this.addLog(this.translate('teleop_log_speed_changed', { mode: (this.speedModes.find(item => item.value === mode) || {}).label }));
      } catch (e) {
        const message = this.translateConnectionLog(e.message || this.translate('teleop_no_response'));
        this.addLog(this.translate('teleop_log_speed_change_failed', { message }));
        this.$Notice.error({ title: this.translate('teleop_notice_title'), desc: this.translate('teleop_speed_change_failed') + ': ' + message });
      } finally {
        this.speedModePending = false;
      }
    },

    // 同步手柄连接状态，手柄断开时停止遥控。
    onGamepadState(state) {
      this.gamepadConnected = !!(state && state.connected);
      this.gamepadName = state && state.id ? state.id : '';
      if (!this.gamepadConnected && this.connection && this.isConnected && this.teleopEnabled) {
        this.connection.setTeleopEnabled(false).catch(() => {});
        this.teleopEnabled = false;
        this.clearAutoDisableTimer();
      }
      this.addLog(this.translate(this.gamepadConnected ? 'teleop_log_gamepad_connected' : 'teleop_log_gamepad_disconnected'));
    },

    // 处理手柄移动提示（未开启遥控时）
    onGamepadMovement(hasMovement) {
      if (hasMovement && !this.teleopEnabled && this.gamepadConnected) {
        this.showGamepadHint = true;
        if (this.gamepadHintTimer) clearTimeout(this.gamepadHintTimer);
        this.gamepadHintTimer = setTimeout(() => {
          this.showGamepadHint = false;
        }, 3000);
      } else if (!hasMovement) {
        this.showGamepadHint = false;
        if (this.gamepadHintTimer) {
          clearTimeout(this.gamepadHintTimer);
          this.gamepadHintTimer = null;
        }
      }
    },

    /* ---------- 日志 ---------- */
    // 根据日志语义返回颜色等级：错误/不匹配、成功、普通信息。
    logClass(msg) {
      const text = String(msg || '')
      if (/不匹配|失败|错误|异常|超时|mismatch|failed|error|timeout/i.test(text)) return 'log-line--error'
      if (/匹配成功|已连接|已开始|已停止|完成|connected|started|stopped|complete|opened/i.test(text)) return 'log-line--success'
      return 'log-line--info'
    },
    // 追加带时间戳的日志并限制日志数量。
    addLog(msg) {
      const time = new Date().toLocaleTimeString(this.lang === 'en' ? 'en-US' : 'zh-CN', {
        hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit'
      });
      this.logs.push(`[${time}] ${msg}`);
      if (this.logs.length > this.maxLogs) {
        this.logs.shift();
      }
    }
  }
};
</script>

<style scoped>
.teleop-layer {
  position: absolute;
  inset: 0;
  z-index: 16;
  container-type: inline-size;
  pointer-events: none; /* 不挡视频点击，子元素单独开启 */
}
.teleop-layer > * {
  pointer-events: auto;
}
.teleop-layer--active .joystick-overlay {
  animation: teleop-expand-in 260ms cubic-bezier(.2,.8,.2,1) both;
}
.teleop-layer--active .teleop-topbar {
  animation: teleop-topbar-expand-in 260ms cubic-bezier(.2,.8,.2,1) both;
}
@keyframes teleop-expand-in {
  from { opacity: 0; transform: translateY(-6px) scale(.96); transform-origin: right top; }
  to { opacity: 1; transform: translateY(0) scale(1); transform-origin: right top; }
}
@keyframes teleop-topbar-expand-in {
  from { opacity: 0; transform: translateX(-50%) translateY(-6px) scale(.96); transform-origin: center top; }
  to { opacity: 1; transform: translateX(-50%) translateY(0) scale(1); transform-origin: center top; }
}

/* 顶部标题栏由视频组件管理；遥控控件固定显示在其下方。 */
.teleop-topbar {
  position: absolute;
  top: 68px;
  left: 50%;
  z-index: 21;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  gap: 8px;
  width: max-content;
  max-width: calc(100% - 24px);
  transform: translateX(-50%);
}

/* ---- 遥控连接和速度胶囊 ---- */
.teleop-statusbar {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 5px 8px 5px 14px;
  border-radius: 999px;
  background: rgba(12, 18, 26, 0.72);
  border: 1px solid rgba(255, 255, 255, 0.14);
  backdrop-filter: blur(10px);
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.4);
  white-space: nowrap;
}

.state-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #8a94a0;
  flex-shrink: 0;
}
.state-dot.connected { background: #00e676; box-shadow: 0 0 7px #00e676; }
.state-dot.connecting,
.state-dot.signalling { background: #ffc107; box-shadow: 0 0 7px #ffc107; }
.state-dot.error,
.state-dot.disconnected { background: #ff5252; box-shadow: 0 0 7px #ff5252; }

.state-text {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.88);
  letter-spacing: 0.5px;
}

.speed-label {
  color: rgba(255, 255, 255, 0.62);
  font-size: 11px;
  margin-left: 2px;
}

/* 胶囊按钮 */
.pill-btn {
  padding: 4px 14px;
  border-radius: 999px;
  border: 1px solid rgba(255, 255, 255, 0.22);
  background: rgba(255, 255, 255, 0.08);
  color: #fff;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.15s ease;
  outline: none;
}
.pill-btn:hover { background: rgba(255, 255, 255, 0.16); }
.pill-btn:active { transform: scale(0.96); }
.pill-btn:disabled { opacity: 0.6; cursor: default; }

.pill-btn--primary {
  background: linear-gradient(135deg, #00c8f5, #0078db);
  border: none;
  box-shadow: 0 2px 8px rgba(0, 150, 220, 0.4);
}
.pill-btn--primary:hover { background: linear-gradient(135deg, #1ed2fc, #0a85e6); }

.pill-btn--active {
  background: rgba(0, 230, 118, 0.18);
  border-color: rgba(0, 230, 118, 0.55);
  color: #2bff8f;
}

.pill-btn--danger {
  background: rgba(255, 82, 82, 0.14);
  border-color: rgba(255, 82, 82, 0.55);
  color: #ff7a7a;
}
.pill-btn--danger:hover { background: rgba(255, 82, 82, 0.24); }

.speed-selector {
  display: inline-flex;
  align-items: center;
  height: 26px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 7px;
  overflow: hidden;
  background: rgba(255, 255, 255, 0.05);
}
.speed-option {
  height: 100%;
  min-width: 34px;
  padding: 0 7px;
  border: 0;
  border-right: 1px solid rgba(255, 255, 255, 0.12);
  background: transparent;
  color: rgba(255, 255, 255, 0.68);
  font-size: 11px;
  cursor: pointer;
}
.speed-option:last-child { border-right: 0; }
.speed-option:hover { background: rgba(255, 255, 255, 0.12); color: #fff; }
.speed-option--selected { background: rgba(0, 200, 245, 0.24); color: #8cefff; }
.speed-option--disabled { cursor: not-allowed; color: rgba(255, 255, 255, 0.34); }
.speed-option:disabled { cursor: wait; opacity: 0.65; }

.pill-close {
  width: 24px;
  height: 24px;
  border: none;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.75);
  font-size: 15px;
  line-height: 1;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s ease;
}
.pill-close:hover { background: rgba(255, 82, 82, 0.3); color: #fff; }

/* ---- PS4 手柄状态 + 日志：与遥控连接操作形成同一控制组 ---- */
.teleop-device-toolbar {
  position: relative;
  display: flex;
  align-items: center;
  gap: 6px;
  min-height: 26px;
  padding: 4px 6px 4px 8px;
  border-radius: 8px;
  background: rgba(0, 0, 0, 0.28);
  border: 1px solid rgba(255, 255, 255, 0.14);
  color: rgba(255, 255, 255, 0.68);
  font-size: 11px;
  white-space: nowrap;
  backdrop-filter: blur(6px);
}
.device-icon {
  width: 17px;
  height: 17px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.device-icon svg {
  width: 17px;
  height: 17px;
}
.device-state-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #ffb020;
}
.teleop-device-toolbar--connected {
  color: #7dffb2;
  border-color: rgba(0, 230, 118, 0.45);
}
.teleop-device-toolbar--connected .device-state-dot {
  background: #00e676;
  box-shadow: 0 0 7px #00e676;
}
.device-log-button {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 22px;
  padding: 0 6px;
  border: 0;
  border-left: 1px solid rgba(255, 255, 255, 0.16);
  background: transparent;
  color: inherit;
  font-size: 11px;
  cursor: pointer;
}
.device-log-button:hover { color: #fff; background: rgba(255, 255, 255, 0.1); }
.device-log-button svg { width: 14px; height: 14px; }

/* ---- 手柄移动提示 ---- */
.teleop-gamepad-hint {
  position: absolute;
  top: 116px;
  left: 50%;
  z-index: 22;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 14px;
  border-radius: 8px;
  background: rgba(255, 152, 0, 0.18);
  border: 1px solid rgba(255, 152, 0, 0.5);
  backdrop-filter: blur(8px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4);
  transform: translateX(-50%);
  animation: teleop-hint-in 260ms ease-out both;
}
@keyframes teleop-hint-in {
  from { opacity: 0; transform: translateX(-50%) translateY(-8px); }
  to { opacity: 1; transform: translateX(-50%) translateY(0); }
}
.hint-icon {
  font-size: 16px;
}
.hint-text {
  color: #ffd79a;
  font-size: 12px;
  font-weight: 500;
}

/* ---- 日志面板 ---- */
.teleop-log {
  position: absolute;
  top: calc(100% + 8px);
  right: 6px;
  z-index: 21;
  width: 240px;
  max-width: calc(100cqw - 24px);
  border-radius: 12px;
  background: rgba(12, 18, 26, 0.8);
  border: 1px solid rgba(255, 255, 255, 0.14);
  backdrop-filter: blur(10px);
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.4);
  overflow: hidden;
  animation: teleop-panel-open 220ms ease-out both;
}
@keyframes teleop-panel-open {
  from { opacity: 0; transform: translateY(-8px) scaleY(.94); transform-origin: right top; }
  to { opacity: 1; transform: translateY(0) scaleY(1); transform-origin: right top; }
}
.log-header {
  display: flex;
  justify-content: space-between;
  padding: 6px 12px;
  font-size: 11px;
  color: rgba(255, 255, 255, 0.85);
  background: rgba(255, 255, 255, 0.06);
  cursor: pointer;
}
.log-body {
  max-height: 120px;
  overflow-y: auto;
  padding: 6px 12px;
}
.log-line {
  font-size: 10px;
  line-height: 1.6;
  color: rgba(255, 255, 255, 0.65);
  font-family: Consolas, Monaco, monospace;
  word-break: break-all;
}
.log-line--error { color: #ff8a8a; }
.log-line--success { color: #7dffb2; }
.log-line--info { color: rgba(255, 255, 255, 0.65); }

/*
 * 低宽度下保留核心状态、速度、连接和关闭动作；控制组会自然换行，
 * 让播放器自己的编号、镜头切换和时间始终保持可见。
 */
@container video-card (max-width: 620px) {
  .teleop-topbar {
    top: 66px;
    gap: 6px;
  }
  .teleop-statusbar {
    gap: 6px;
    padding-left: 10px;
    padding-right: 6px;
  }
  .speed-label { display: none; }
  .speed-option {
    min-width: 30px;
    padding: 0 5px;
  }
  .pill-btn { padding-left: 11px; padding-right: 11px; }
}

@container video-card (max-width: 460px) {
  .teleop-topbar { max-width: calc(100% - 16px); }
  .teleop-gamepad-hint { top: 154px; }
}

@container video-card (min-width: 800px) and (max-width: 1399px) {
  .teleop-topbar--en {
    left: calc(50% + 48px);
  }
}

/* 仅当视频卡片有完整横向空间时，控制组才与镜头切换同行。 */
@container video-card (min-width: 1400px) {
  .teleop-topbar {
    top: 15px;
    left: calc(25% + 180px);
    width: max-content;
    max-width: calc(75% - 480px);
    flex-wrap: nowrap;
    transform: none;
  }
  .teleop-layer--active .teleop-topbar {
    animation: teleop-topbar-inline-expand-in 260ms cubic-bezier(.2,.8,.2,1) both;
  }
  .teleop-gamepad-hint,
  .teleop-log {
    top: 56px;
    left: calc(25% + 180px);
    transform: none;
  }
  .teleop-log {
    top: calc(100% + 8px);
    right: 6px;
    left: auto;
  }
  .teleop-topbar--en {
    left: calc(25% + 236px);
  }
}

@keyframes teleop-topbar-inline-expand-in {
  from { opacity: 0; transform: translateY(-6px) scale(.96); transform-origin: left top; }
  to { opacity: 1; transform: translateY(0) scale(1); transform-origin: left top; }
}
</style>
